import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Grid, message, Modal } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
import { type LocalMakeWithPermissionsSpecsFragment } from '../../../api/fragments/LocalMakeWithPermissionsSpecs';
import {
    DeleteVehicleDocument,
    type DeleteVehicleMutation,
    type DeleteVehicleMutationVariables,
} from '../../../api/mutations/deleteVehicle';
import { useUpdateMakeMutation } from '../../../api/mutations/updateMake';
import { type LocalMakeSettings } from '../../../api/types';
import Form from '../../../components/fields/Form';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import MakeDetailsForm from './MakeDetailsForm';
import useMakeFormValidator from './useMakeFormValidator';

export type MakeDetailsInnerPageProps = { make: LocalMakeWithPermissionsSpecsFragment };
export type MakeDetailsFormValues = LocalMakeSettings & {
    moduleId: string;
};

export const defaultValues: MakeDetailsFormValues = {
    moduleId: null,
    identifier: '',
    name: { defaultValue: '', overrides: [] },
    order: null,
    isActive: true,
};

const MakeDetailsInnerPage = ({ make }: MakeDetailsInnerPageProps) => {
    const [updateMake] = useUpdateMakeMutation();
    const { t } = useTranslation('makeDetails');
    const validator = useMakeFormValidator();
    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const screens = Grid.useBreakpoint();
    const { updated, offset } = useFormattedSimpleVersioning({ versioning: make.versioning, moduleId: make.moduleId });

    const makeId = make.id;

    const onSubmit = useHandleError(
        async (values: MakeDetailsFormValues) => {
            // submitting message
            message.loading({
                content: t('makeDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });
            const { moduleId, ...settings } = values;

            // submit update
            await updateMake({ variables: { id: makeId, settings } }).finally(() => {
                message.destroy('primary');
            });

            // inform about success
            message.success({
                content: t('makeDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            navigate('/admin/vehicles/makes');
        },
        [makeId, navigate, t, updateMake]
    );

    const onDelete = useCallback(() => {
        Modal.confirm({
            title: t('makeDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('makeDetails:deleteModal.content'),
            okText: t('makeDetails:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('makeDetails:deleteModal.cancelText'),
            async onOk() {
                try {
                    message.loading({
                        content: t('makeDetails:message.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with API
                    await apolloClient
                        .mutate<DeleteVehicleMutation, DeleteVehicleMutationVariables>({
                            mutation: DeleteVehicleDocument,
                            variables: { id: makeId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    message.success({
                        content: t('makeDetails:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    navigate('/admin/vehicles/makes');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [apolloClient, t, makeId, navigate]);

    const initialValues = useMemo(
        (): MakeDetailsFormValues => ({ ...pick(['moduleId', 'identifier', 'name', 'order', 'isActive'], make) }),
        [make]
    );

    const [hasUpdatePermission, hasDeletePermission] = useMemo(
        () => [
            hasPermissions(make.permissions, [permissionKind.updateVehicle]),
            hasPermissions(make.permissions, [permissionKind.deleteVehicle]),
        ],
        [make]
    );

    return (
        <ConsolePageWithHeader
            extra={
                hasDeletePermission && (
                    <Button onClick={onDelete} danger>
                        {t('makeDetails:actions.delete')}
                    </Button>
                )
            }
            footer={[
                hasUpdatePermission && (
                    <Button key="submit" form="makeDetailsForm" htmlType="submit" type="primary">
                        {t('makeDetails:actions.update')}
                    </Button>
                ),
            ]}
            onBack={() => navigate('/admin/vehicles/makes')}
            title={
                screens.md
                    ? t('makeDetails:title', { name: initialValues.name.defaultValue, id: initialValues.identifier })
                    : `${initialValues.name.defaultValue} ${initialValues.identifier}`
            }
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validator}>
                {({ handleSubmit }) => (
                    <Form id="makeDetailsForm" name="makeDetailsForm" onSubmitCapture={handleSubmit}>
                        <MakeDetailsForm disabled={!hasUpdatePermission} offset={offset} updated={updated} />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default MakeDetailsInnerPage;
